import { useState } from "react";
import { X, Loader2, Printer } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import PrinterSelectModal from "./PrinterSelectModal";
import FilterPills from "./admin/FilterPills";
import {
    getSavedNetworkPrinter, saveNetworkPrinter, submitNetworkPrintJob,
} from "./helpers/networkPrint";
import {
    buildBayLocationLabel, buildCompactBayLabel, COMPACT_BAY_LABEL_BASE,
    buildLargeSlotLabel, LARGE_SLOT_LABEL_BASE, LARGE_SLOT_LABEL_152_BASE,
    BAND_6X4_LABEL_BASE, BAND_6X4_CONTENT,
    COMPACT_LABEL_ORIENTATIONS, type CompactLabelOrientation,
    type SlotLabelStyle, SLOT_LABEL_STYLES,
    buildLabelSheet, LABELS_PER_PRINT_JOB,
    silentPrint, browserPrint,
    getSavedLocalPrinter, clearLocalPrinter,
    savePrintModePreference, clearPrintModePreference,
    BAY_LABEL_BASE,
} from "./helpers/printAgent";
import { selectSlots } from "./helpers/slotSelection";
import { buildRackingStripLabel } from "./helpers/rackingStripLabel";
import { buildToteLabel } from "./helpers/toteLabel";

// Print every slot label on one sorting bay to a chosen printer, at the label
// stock size configured for that warehouse.
//
// Also reused by Warehouse › Locations for the high/low racking labels (same
// designs, same printer plumbing): that caller passes its own title/hint,
// turns the typed slot range off (the page's filters choose the labels) and
// persists the size to its own per-warehouse setting via onSaveSize.
//
// Two things are deliberately scoped differently:
//   • the PRINTER is per-device (localStorage, shared with every other print
//     button in the app) — a desk keeps whatever printer it's plugged into;
//   • the label SIZE is per-warehouse (settings.sorting_label_*_mm) — a site
//     buys one roll of stock and every bay in it prints the same.

export type BayLabel = {
    barcode: string; label: string; shelf: string; position: number | string;
    shelf_colour?: string | null; position_colour?: string | null;
    /** Which of the caller's `groups` this label belongs to (racking: high/low). */
    group?: string | null;
    /** What the racking strip design prints, verbatim. */
    aisle?: string | null; bay?: string | null; level?: string | null;
};

/** A quick filter offered above the selectable list. */
export type LabelGroup = { key: string; label: string };

/** The sorting slot designs plus the racking strip (aisle | bay | level |
 *  barcode) and the tote label (bars over the tote ID). Which are offered is
 *  the caller's choice via `styles`. */
export type LabelStyle = SlotLabelStyle | "racking" | "tote";

type ResizableStyle = "standard" | "racking" | "tote";

// The designs whose size follows the caller's saved size rather than being
// fixed by the design.
const isResizable = (s: LabelStyle): s is ResizableStyle => s === "standard" || s === "racking" || s === "tote";

type Mm = { widthMm: number; heightMm: number };

// Sizes are held as exact inch conversions (152.4, 101.6), which read badly in
// a sentence. Show whole millimetres unless the fraction is real.
const mmText = (n: number) => String(Math.round(n * 10) / 10);

// The designs whose size is fixed by the design rather than by the
// per-warehouse setting. Keyed so the size switch, the orientation hint and
// the print call can't drift apart.
//
//   stock   — the physical media, and what the print agent is told to expect.
//   content — the area the design actually draws into. Same as the stock for
//             every design except the 6x4 band, which draws a 45mm strip on a
//             101.6mm label so there's a clear gap either side of it.
//   defaultOrientation — how this design sits on stock as it is normally
//             loaded. The 6x4 is the dispatch shipping-label media, loaded
//             PORTRAIT, so printing content that reads across its long edge is
//             a quarter-turn; defaulting it to "normal" would print off the
//             end of the label.
const FIXED_DESIGNS: Record<Exclude<LabelStyle, ResizableStyle>, {
    stock: Mm; content: Mm; defaultOrientation: CompactLabelOrientation;
}> = {
    compact: { stock: COMPACT_BAY_LABEL_BASE, content: COMPACT_BAY_LABEL_BASE, defaultOrientation: "normal" },
    large: { stock: LARGE_SLOT_LABEL_BASE, content: LARGE_SLOT_LABEL_BASE, defaultOrientation: "normal" },
    large152: { stock: LARGE_SLOT_LABEL_152_BASE, content: LARGE_SLOT_LABEL_152_BASE, defaultOrientation: "normal" },
    band6x4: { stock: BAND_6X4_LABEL_BASE, content: BAND_6X4_CONTENT, defaultOrientation: "cw" },
};

interface Props {
    warehouse: string;
    /** Named in the default title; the caller can pass `title` instead. */
    bayName?: string;
    labels: BayLabel[];
    /** Current per-warehouse stock size; null = the built-in 185×35mm. */
    widthMm: number | null;
    heightMm: number | null;
    /** Persist a new size for this warehouse. Resolves false if the save
     *  failed, "forbidden" when the user may not change it (HTTP 403). */
    onSaveSize: (widthMm: number | null, heightMm: number | null) => Promise<boolean | "forbidden">;
    onClose: () => void;
    /** Header text. Defaults to "Print Labels — {bayName}". */
    title?: string;
    /** Intro line. Defaults to the sorting-bay wording. */
    hint?: string;
    /** Offer the typed slot range (A1, A1-D6). Sorting bays only — the racking
     *  labels are chosen by the Locations page filters instead. Default on. */
    showRange?: boolean;
    /** List every label as a tickable row — all ticked to start — and print
     *  only the ticked ones. Off by default (sorting bays use the range). */
    selectable?: boolean;
    /** Stock the Standard design falls back to when the warehouse has no size
     *  saved, and what its size fields show as placeholders. Defaults to the
     *  185 × 35mm sorting strip; the racking print passes its 80 × 35mm. */
    defaultSize?: Mm;
    /** Pills that narrow the selectable list to labels whose `group` matches
     *  (racking passes High / Low). Nothing is shown when empty. */
    groups?: LabelGroup[];
    /** Designs offered, in menu order; the first is preselected. Defaults to
     *  the sorting slot designs. The racking print puts "racking" first. */
    styles?: LabelStyle[];
    /** Racking levels a single tick box can leave out of the run — the
     *  Locations page passes [4, 5]. Matched on the number in the label's
     *  `level` ("L4" or "4"). The box only shows while some label is on one
     *  of these levels. */
    skipLevels?: number[];
}

// The number in a racking level ("L4" → 4, "4" → 4); NaN when unnumbered.
const levelNumber = (l: BayLabel) => Number((/\d+/.exec(l.level ?? "") ?? [NaN])[0]);

const SortingLabelPrintModal = ({ warehouse, bayName, labels, widthMm, heightMm, onSaveSize, onClose, title, hint, showRange = true, selectable = false, defaultSize, groups = [], styles = SLOT_LABEL_STYLES, skipLevels = [] }: Props) => {
    const { t } = useTranslation();
    const base: Mm = defaultSize ?? BAY_LABEL_BASE;
    const [width, setWidth] = useState(widthMm == null ? "" : String(widthMm));
    const [height, setHeight] = useState(heightMm == null ? "" : String(heightMm));
    // Which label design. "standard" is the 185x35 colour label; "compact" is
    // the 93x47 mono one for a thermal printer; "large" is the 70x30 mono one
    // that shows only the display value and the bars; "large152" is that
    // design scaled up for 152x70 stock; "band6x4" is it again on the 6x4
    // dispatch stock; "racking" is the aisle | bay | level | barcode strip from
    // the racking. All but "standard" and "racking" have their
    // size fixed by the design rather than by the per-warehouse setting, so the
    // width/height fields don't apply to them.
    const [style, setStyle] = useState<LabelStyle>(styles[0] ?? "standard");
    const fixedDesign = isResizable(style) ? null : FIXED_DESIGNS[style as Exclude<LabelStyle, ResizableStyle>];
    // How a mono label sits on the stock — depends on which way the labels feed
    // through the thermal printer, so it has to be selectable. Changing design
    // resets it, because the right answer differs per design (the 6x4 prints on
    // portrait-loaded stock) and a value carried over from the previous design
    // would silently print off the label.
    const [orientation, setOrientation] = useState<CompactLabelOrientation>("normal");
    const changeStyle = (next: LabelStyle) => {
        setStyle(next);
        if (!isResizable(next)) setOrientation(FIXED_DESIGNS[next as Exclude<LabelStyle, ResizableStyle>].defaultOrientation);
    };
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [done, setDone] = useState(0);
    // Which slots to print. Blank = the whole bay.
    const [range, setRange] = useState("");
    const selection = showRange ? selectSlots(range, labels) : { ok: true as const, slots: labels };
    const inScope = selection.ok ? selection.slots : [];
    // One tick box leaves the caller's `skipLevels` (racking: 4 & 5) out of
    // the run. It's a filter on top of the list rather than a bulk untick,
    // so Select all can't quietly bring those levels back while the box still
    // says they're out (Alex, 2026-10-15). Off by default: print the lot.
    const [skipOn, setSkipOn] = useState(false);
    const onSkipLevel = (l: BayLabel) => skipLevels.includes(levelNumber(l));
    const skippable = skipLevels.length ? inScope.filter(onSkipLevel) : [];
    const inRange = skipOn && skippable.length ? inScope.filter(l => !onSkipLevel(l)) : inScope;
    // Tick state for the selectable list, keyed by barcode. Everything starts
    // ticked so the default is still "print the lot".
    const [checked, setChecked] = useState<Set<string>>(() => new Set(labels.map(l => l.barcode)));
    const [listFilter, setListFilter] = useState("");
    const [groupFilter, setGroupFilter] = useState("");
    const q = listFilter.trim().toLowerCase();
    const inGroup = groupFilter ? inRange.filter(l => (l.group ?? "") === groupFilter) : inRange;
    const listed = q
        ? inGroup.filter(l => [l.label, l.barcode, `${l.shelf}${l.position}`, `${l.shelf} ${l.position}`]
            .some(v => String(v).toLowerCase().includes(q)))
        : inGroup;
    const groupCount = (key: string) => inRange.filter(l => (l.group ?? "") === key).length;
    // Select all / Clear act on the rows currently LISTED, so "filter to
    // aisle 3, select all" does what it says.
    const setListed = (on: boolean) => setChecked(c => {
        const next = new Set(c);
        for (const l of listed) { if (on) next.add(l.barcode); else next.delete(l.barcode); }
        return next;
    });
    const toggle = (barcode: string, on: boolean) => setChecked(c => {
        const next = new Set(c);
        if (on) next.add(barcode); else next.delete(barcode);
        return next;
    });
    const selected = selectable ? inRange.filter(l => checked.has(l.barcode)) : inRange;
    // Re-read on each render so the label updates as soon as the picker closes.
    const [printerTick, setPrinterTick] = useState(0);
    const network = getSavedNetworkPrinter();
    const local = getSavedLocalPrinter();
    const printerName = network?.friendlyName || network?.printerName || local || null;

    const parseMm = (raw: string) => {
        const n = Number(raw.trim());
        if (!raw.trim() || !Number.isFinite(n) || n < 20 || n > 300) return null;
        return Math.round(n);
    };
    // Blank or out-of-range falls back to the built-in stock, matching the BE's
    // sanitiseLabelMm (NULL below 20mm) so what prints is what gets saved.
    // The media the print agent is told to expect…
    const stock: Mm = fixedDesign
        ? fixedDesign.stock
        : {
            widthMm: parseMm(width) ?? base.widthMm,
            heightMm: parseMm(height) ?? base.heightMm,
        };
    // …and the area the design draws into, which is smaller for the 6x4 band.
    const content: Mm = fixedDesign ? fixedDesign.content : stock;

    // ONE PRINT JOB PER RUN — deliberate, and the reverse of what this did
    // before.
    //
    // Every selected label is rendered with its single-label builder and the
    // documents are merged by buildLabelSheet into ONE multi-page job (runs
    // longer than LABELS_PER_PRINT_JOB go as a few jobs). A printer that cuts
    // at the end of a job — the Epson TM-C3500 that prints Pembroke's racking
    // strips — then cuts ONCE, after the last label, so A1–A16 comes off as a
    // single strip (Alex, 2026-10-02). Its driver's auto-cut must be set to
    // the end of the document rather than every page; that is a printer
    // setting, not something the Portal can send. The previous one-job-per-
    // label behaviour existed precisely to get a cut after every label.
    //
    // The bay label can't go down the ZPL fast path (coloured blocks + vector
    // arrow), so the barcode value is deliberately withheld from the network
    // job and silentPrint is used rather than silentPrintBarcode — both force
    // the HTML render, which is what honours the @page size.
    const buildOne = (l: BayLabel): string => {
        const common = {
            barcode: l.barcode, label: l.label, shelf: l.shelf, position: l.position,
            shelfColour: l.shelf_colour, positionColour: l.position_colour,
            size: content,
        };
        return style === "tote"
            ? buildToteLabel({ toteId: l.barcode, size: content })
            : style === "racking"
                ? buildRackingStripLabel({ barcode: l.barcode, aisle: l.aisle ?? "", bay: l.bay ?? "", level: l.level ?? "", size: content })
                : style === "compact"
                    ? buildCompactBayLabel({ ...common, orientation })
                    : style === "large" || style === "large152" || style === "band6x4"
                        ? buildLargeSlotLabel({ ...common, orientation, page: stock })
                        : buildBayLocationLabel(common);
    };

    // Saved network printer → this machine's print agent → browser dialog.
    const sendJob = async (html: string): Promise<boolean> => {
        if (network) {
            const r = await submitNetworkPrintJob(network.agentId, network.printerName, html, 1, null, false, {
                labelWidthMm: stock.widthMm, labelHeightMm: stock.heightMm,
            });
            if (r.success) return true;
        }
        const r = await silentPrint(html, 1, local ?? undefined);
        if (r.success) return true;
        return (await browserPrint(html)).success;
    };

    const handlePrint = async () => {
        if (!selected.length) return;
        setPrinting(true);
        setDone(0);
        try {
            // Persist the size first — if that fails, stop rather than print a
            // run at a size the warehouse won't remember next time.
            const nextW = parseMm(width);
            const nextH = parseMm(height);
            if (isResizable(style) && (nextW !== widthMm || nextH !== heightMm)) {
                const saved = await onSaveSize(nextW, nextH);
                if (saved !== true) {
                    // A refusal is not a server fault: say who can change it
                    // (2026-10-20, a Team Leader saw "Server error" here).
                    toast.error(t(saved === "forbidden" ? "label_size_forbidden" : "server_error"), { toastId: "sorting-label-size-err" });
                    return;
                }
            }
            let failed = 0;
            for (let i = 0; i < selected.length; i += LABELS_PER_PRINT_JOB) {
                const batch = selected.slice(i, i + LABELS_PER_PRINT_JOB);
                if (!await sendJob(buildLabelSheet(batch.map(buildOne)))) failed += batch.length;
                setDone(d => d + batch.length);
            }
            if (failed === selected.length) toast.error(t("print_failed"), { toastId: "sorting-print-err" });
            else if (failed > 0) toast.warn(t("print_partial_failed", { failed: String(failed), total: String(selected.length) }));
            else { toast.success(t("printing")); onClose(); }
        } finally {
            setPrinting(false);
        }
    };

    return (
        <>
            <div className="modal-overlay" onClick={() => !printing && onClose()}>
                <div className="modal-card" onClick={e => e.stopPropagation()}>
                    <div className="modal-card-header">
                        <h2>{title ?? `${t("sorting_print_labels_title")} — ${bayName ?? ""}`}</h2>
                        <button className="modal-close" onClick={onClose} disabled={printing}><X size={18} /></button>
                    </div>
                    <div className="modal-card-body">
                        <p className="admin-settings-section-hint" style={{ marginBottom: 12 }}>
                            {hint ?? t("sorting_print_labels_hint", { count: String(labels.length), warehouse })}
                        </p>

                        {showRange && (
                        <label className="dispatch-admin-form-label" style={{ display: "block", marginBottom: 12 }}>
                            {t("sorting_print_range")}
                            <input type="text" className="dispatch-admin-form-input" style={{ width: "100%" }}
                                placeholder={t("sorting_print_range_placeholder")}
                                value={range} disabled={printing}
                                onChange={e => setRange(e.target.value)} />
                            <span className="admin-settings-section-hint" style={{ display: "block", marginTop: 4 }}>
                                {!selection.ok
                                    ? <span style={{ color: "var(--color-danger, #dc2626)" }}>
                                        {t("sorting_print_range_invalid", { token: selection.badToken })}
                                      </span>
                                    : inRange.length === 0
                                        ? <span style={{ color: "var(--color-danger, #dc2626)" }}>{t("sorting_print_range_none")}</span>
                                        : t("sorting_print_range_hint", { count: String(inRange.length) })}
                            </span>
                        </label>
                        )}

                        {selectable && (
                        <div style={{ marginBottom: 12 }}>
                            <div className="dispatch-admin-form-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ flex: 1 }}>{t("label_print_pick")}</span>
                                <button type="button" className="btn btn-ghost btn-sm" disabled={printing || listed.length === 0}
                                    onClick={() => setListed(true)}>
                                    {t("label_print_pick_all")}
                                </button>
                                <button type="button" className="btn btn-ghost btn-sm" disabled={printing || listed.length === 0}
                                    onClick={() => setListed(false)}>
                                    {t("label_print_pick_none")}
                                </button>
                            </div>
                            {groups.length > 0 && (
                                <FilterPills
                                    className="label-print-pick-pills"
                                    value={groupFilter}
                                    onChange={setGroupFilter}
                                    allCount={inRange.length}
                                    options={groups.map(g => ({ value: g.key, label: g.label, count: groupCount(g.key) }))}
                                />
                            )}
                            {skippable.length > 0 && (
                                <label className="label-print-skip-levels">
                                    <input type="checkbox" checked={skipOn} disabled={printing}
                                        onChange={e => setSkipOn(e.target.checked)} />
                                    {t("label_print_skip_levels", {
                                        levels: skipLevels.join(" & "),
                                        count: String(skippable.length),
                                    })}
                                </label>
                            )}
                            <input type="search" className="dispatch-admin-form-input" style={{ width: "100%", marginTop: 4 }}
                                placeholder={t("label_print_pick_filter")}
                                value={listFilter} disabled={printing}
                                onChange={e => setListFilter(e.target.value)} />
                            <div className="label-print-pick-list">
                                {listed.map(l => (
                                    <label key={l.barcode} className="label-print-pick-row">
                                        <input type="checkbox" checked={checked.has(l.barcode)} disabled={printing}
                                            onChange={e => toggle(l.barcode, e.target.checked)} />
                                        <span className="label-print-pick-name">{l.label}</span>
                                        <span className="label-print-pick-slot">{l.shelf} {l.position}</span>
                                        <span className="label-print-pick-code">{l.barcode}</span>
                                    </label>
                                ))}
                                {listed.length === 0 && (
                                    <div className="admin-settings-section-hint label-print-pick-empty">{t("label_print_pick_empty")}</div>
                                )}
                            </div>
                            <span className="admin-settings-section-hint" style={{ display: "block", marginTop: 4 }}>
                                {t("label_print_pick_count", { count: String(selected.length), total: String(inRange.length) })}
                            </span>
                        </div>
                        )}

                        <label className="dispatch-admin-form-label" style={{ display: "block", marginBottom: 12 }}>
                            {t("select_printer")}
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                                <span style={{ flex: 1, fontSize: 13 }}>
                                    {printerName ?? t("printer_default_device")}
                                </span>
                                <button className="btn btn-ghost btn-sm" onClick={() => setShowPrinterSelect(true)} disabled={printing}>
                                    {t("printer_select")}
                                </button>
                            </div>
                        </label>

                        <label className="dispatch-admin-form-label" style={{ display: "block", marginBottom: 12 }}>
                            {t("sorting_label_style")}
                            <select className="dispatch-admin-form-input" style={{ width: "100%" }}
                                value={style} disabled={printing}
                                onChange={e => changeStyle(e.target.value as LabelStyle)}>
                                {styles.map(s => (
                                    <option key={s} value={s}>
                                        {isResizable(s)
                                            ? t(`label_style_${s}_at`, { w: mmText(base.widthMm), h: mmText(base.heightMm) })
                                            : t(`sorting_label_style_${s}`)}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {fixedDesign ? (<>
                            <label className="dispatch-admin-form-label" style={{ display: "block", marginBottom: 8 }}>
                                {t("sorting_label_orientation")}
                                <select className="dispatch-admin-form-input" style={{ width: "100%" }}
                                    value={orientation} disabled={printing}
                                    onChange={e => setOrientation(e.target.value as CompactLabelOrientation)}>
                                    {COMPACT_LABEL_ORIENTATIONS.map(o => (
                                        <option key={o} value={o}>{t(`sorting_label_orientation_${o}`, {
                                            // Across-the-page dimension: the design's own width, or its
                                            // height once a quarter-turn swaps the footprint.
                                            across: mmText(o === "cw" || o === "ccw"
                                                ? fixedDesign.stock.heightMm
                                                : fixedDesign.stock.widthMm),
                                        })}</option>
                                    ))}
                                </select>
                            </label>
                            <p className="admin-settings-section-hint">
                                {t(`sorting_label_style_${style}_hint`, {
                                    w: mmText(fixedDesign.stock.widthMm),
                                    h: mmText(fixedDesign.stock.heightMm),
                                    band: mmText(fixedDesign.content.heightMm),
                                    gap: mmText((fixedDesign.stock.heightMm - fixedDesign.content.heightMm) / 2),
                                })}
                            </p>
                        </>) : (<>
                        <div className="dispatch-admin-form-grid">
                            <label className="dispatch-admin-form-label">
                                {t("sorting_label_width_mm")}
                                <input type="number" inputMode="numeric" min={20} max={300}
                                    className="dispatch-admin-form-input"
                                    placeholder={String(base.widthMm)}
                                    value={width} disabled={printing}
                                    onChange={e => setWidth(e.target.value)} />
                            </label>
                            <label className="dispatch-admin-form-label">
                                {t("sorting_label_height_mm")}
                                <input type="number" inputMode="numeric" min={20} max={300}
                                    className="dispatch-admin-form-input"
                                    placeholder={String(base.heightMm)}
                                    value={height} disabled={printing}
                                    onChange={e => setHeight(e.target.value)} />
                            </label>
                        </div>
                        <p className="admin-settings-section-hint" style={{ marginTop: 8 }}>
                            {t("sorting_label_size_hint", {
                                w: mmText(base.widthMm), h: mmText(base.heightMm),
                            })}
                        </p>
                        </>)}

                        <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                            <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={printing}>
                                {t("cancel")}
                            </button>
                            <button className="btn btn-success btn-sm" onClick={handlePrint} disabled={printing || selected.length === 0}>
                                {printing
                                    ? <><Loader2 size={14} className="dispatch-admin-spin" /> {done}/{selected.length}</>
                                    : <><Printer size={14} /> {t("sorting_print_n", { count: String(selected.length) })}</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={false}
                    printMode={network ? "network" : local ? "local" : null}
                    onSelect={p => { saveNetworkPrinter(p); savePrintModePreference("network"); setPrinterTick(printerTick + 1); setShowPrinterSelect(false); }}
                    onConnectLocal={() => { savePrintModePreference("local"); setPrinterTick(printerTick + 1); setShowPrinterSelect(false); }}
                    onDisconnect={() => { clearLocalPrinter(); clearPrintModePreference(); setPrinterTick(printerTick + 1); }}
                    onClose={() => { setPrinterTick(printerTick + 1); setShowPrinterSelect(false); }}
                />
            )}
        </>
    );
};

export default SortingLabelPrintModal;
